<template>
  <div class="page-container billing-welcome-page">
    <portal to="page-name">Faturamento</portal>
    <div class="card card-page">
      <div class="card-header">
        <h1 class="card-title">Novo faturamento de convênios</h1>
      </div>
      <div class="card-body">
        <div class="bw-hero">
          <div class="bw-hero-text">
            <span class="bw-eyebrow">Novo módulo</span>
            <h2>Da guia ao recebimento, sem planilha no meio</h2>
            <p>
              Cada atendimento vira uma guia no padrão TISS. As guias entram em lotes com
              número da operadora, e o fechamento do lote já cria o título a receber e o
              repasse de cada profissional. O retorno da operadora entra por demonstrativo,
              a glosa aparece com o motivo da Tabela 38 e o recurso sai em XML.
            </p>
            <template v-if="canEnable">
              <router-link class="btn btn-primary btn-icon btn-icon-left"
                           :to="actionRoute">
                <fa-icon :icon="['fal', 'cog']"/>{{ actionLabel }}
              </router-link>
              <div class="bw-hint" v-if="!cashRegisterEnabled">
                O novo faturamento exige o caixa novo ligado: o particular é recebido no caixa
                e só as guias de convênio entram em lote.
              </div>
            </template>
            <div class="bw-hint" v-else>
              Peça a quem administra a clínica para ativar em Faturamento › Configuração.
            </div>
          </div>
          <div class="bw-hero-side">
            <div class="bw-step" v-for="step in steps" :key="step.label">
              <span class="bw-step-icon"><fa-icon :icon="['fal', step.icon]"/></span>
              <div>
                <strong>{{ step.label }}</strong>
                <small>{{ step.text }}</small>
              </div>
            </div>
          </div>
        </div>

        <div class="settings-section">O que você ganha</div>
        <div class="bw-features">
          <div class="bw-feature" v-for="feature in features" :key="feature.title">
            <span class="bw-feature-icon"><fa-icon :icon="['fal', feature.icon]"/></span>
            <strong>{{ feature.title }}</strong>
            <p>{{ feature.text }}</p>
          </div>
        </div>

        <div class="settings-section">Como os dois convivem</div>
        <div class="bw-compare">
          <div class="bw-side bw-side-legacy">
            <div class="bw-side-head">
              <span class="bw-side-tag">Continua disponível</span>
              <strong>Faturamento antigo</strong>
              <small>Financeiro › Faturas, guias e repasses</small>
            </div>
            <ul>
              <li><fa-icon :icon="['fal', 'eye']"/>Consultar as guias e faturas de sempre</li>
              <li><fa-icon :icon="['fal', 'pencil']"/>Editar o que ainda está em aberto</li>
              <li><fa-icon :icon="['fal', 'print']"/>Imprimir guias, faturas e demonstrativos</li>
              <li><fa-icon :icon="['fal', 'receipt']"/>Concluir os repasses já lançados</li>
            </ul>
          </div>

          <div class="bw-side bw-side-new">
            <div class="bw-side-head">
              <span class="bw-side-tag is-new">A partir da virada</span>
              <strong>Novo faturamento</strong>
              <small>Menu Faturamento</small>
            </div>
            <ul>
              <li><fa-icon :icon="['fal', 'check-circle']"/>
                <span><b>Você monta o lote</b>: escolhe as guias, confere o total e fecha —
                  nada é agrupado automaticamente</span>
              </li>
              <li><fa-icon :icon="['fal', 'layer-group']"/>Número TISS do convênio a cada lote</li>
              <li><fa-icon :icon="['fal', 'file-invoice-dollar']"/>
                Título a receber e repasses criados no fechamento
              </li>
              <li><fa-icon :icon="['fal', 'gavel']"/>Glosa, recurso e baixa pelo demonstrativo</li>
            </ul>
          </div>
        </div>

        <div class="bw-migration">
          <fa-icon :icon="['fal', 'exchange']" class="bw-migration-icon"/>
          <div>
            <strong>São separados, e não há conversão automática.</strong>
            O módulo novo não mostra nada do antigo, e o antigo não enxerga o que nasce no novo.
            Termine no antigo o que está em aberto — feche as faturas, envie os lotes e conclua
            os repasses — e, a partir da data que você escolher, lance tudo no novo.
          </div>
        </div>

        <div class="settings-training">
          <a class="settings-training-media" :href="trainingVideoUrl" target="_blank"
             rel="noopener" v-if="trainingVideoId" title="Assistir no YouTube">
            <img src="../../../../assets/images/capa-azul.jpg"
                 alt="Treinamento do novo faturamento">
            <span class="settings-training-play">
              <fa-icon :icon="['fas', 'play']"/>
            </span>
          </a>
          <div class="settings-training-media" v-else>
            <div class="settings-training-placeholder">
              <fa-icon :icon="['fal', 'video']" size="2x"/>
              <span>Vídeo em breve</span>
            </div>
          </div>
          <div class="settings-training-body">
            <span class="settings-training-eyebrow">Treinamento</span>
            <div class="settings-training-title">Como usar o novo faturamento</div>
            <p>
              Da guia ao lote, envio do XML, importação do demonstrativo, glosa e recurso,
              baixa do título e repasse dos profissionais. Assista antes de ligar para a equipe.
            </p>
            <a :href="trainingVideoUrl" target="_blank" rel="noopener" v-if="trainingVideoId">
              <fa-icon :icon="['fab', 'youtube']"/> Abrir no YouTube
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { UPDATE_BRANCH } from '@/data/actions/modules/general';

const TRAINING_VIDEO_ID = '';

export default {
  data() {
    return {
      trainingVideoId: TRAINING_VIDEO_ID,
      steps: [
        { icon: 'file-medical-alt', label: 'Guia', text: 'nasce do atendimento, no padrão TISS' },
        { icon: 'layer-group', label: 'Lote', text: 'número da operadora e XML congelado' },
        { icon: 'file-import', label: 'Demonstrativo', text: 'retorno da operadora importado' },
        { icon: 'gavel', label: 'Glosa e recurso', text: 'motivo da Tabela 38 e recurso em XML' },
        { icon: 'receipt', label: 'Repasse', text: 'por profissional, com ajuste de glosa' },
      ],
      features: [
        {
          icon: 'shield-check',
          title: 'Nada gravado pela metade',
          text: 'Fechar o lote cria guia travada, título a receber e repasses numa operação só: '
            + 'se algo falhar, nada é gravado.',
        },
        {
          icon: 'gavel',
          title: 'Glosa deixa de ser perda silenciosa',
          text: 'O demonstrativo entra por XML, cada glosa fica com código e valor, e o prazo '
            + 'de recurso do convênio aparece antes de vencer.',
        },
        {
          icon: 'calculator',
          title: 'Repasse com a conta aberta',
          text: 'O cálculo mostra de onde saiu cada valor, e a glosa que chega depois do '
            + 'pagamento vira ajuste no próximo repasse.',
        },
        {
          icon: 'calendar-check',
          title: 'Previsão de recebimento',
          text: 'A regra de pagamento de cada convênio calcula quando o lote entra, seja por '
            + 'dias após o envio, dia fixo ou cronograma.',
        },
        {
          icon: 'chart-line',
          title: 'Dashboard e 22 relatórios',
          text: 'Produção, faturado, glosas, prazo médio de recebimento e repasse, com clique '
            + 'que leva para a lista de origem.',
        },
        {
          icon: 'file-invoice-dollar',
          title: 'Particular fora do convênio',
          text: 'Atendimento particular é recebido no caixa, sem virar guia — o faturamento '
            + 'cuida só do que vai para a operadora.',
        },
      ],
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    canEnable() {
      return this.$can(UPDATE_BRANCH);
    },
    cashRegisterEnabled() {
      const settings = this.user && this.user.branch ? this.user.branch.settings : null;
      return !!(settings && settings.cashRegister && settings.cashRegister.enabled);
    },
    actionRoute() {
      return this.cashRegisterEnabled
        ? '/financial/billing-batches/settings'
        : '/cash-register/settings';
    },
    actionLabel() {
      return this.cashRegisterEnabled ? 'Ativar nesta filial' : 'Ligar o caixa novo primeiro';
    },
    trainingVideoUrl() {
      return `https://www.youtube.com/watch?v=${this.trainingVideoId}`;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables";

.billing-welcome-page {
  .bw-eyebrow {
    color: $primary-color;
    font-size: $font-size-xs;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
  }
  .bw-hero {
    align-items: start;
    display: grid;
    gap: $layout-spacing-lg * 2;
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
    padding: $layout-spacing-sm 0 $layout-spacing-lg;
    h2 {
      font-size: 1.25rem;
      font-weight: 600;
      line-height: 1.3;
      margin: $layout-spacing-sm 0 0;
      max-width: 22em;
    }
    p {
      color: $gray-color-dark;
      line-height: 1.6;
      margin: $layout-spacing-sm 0 0;
      max-width: 44em;
    }
    .btn {
      margin-top: $layout-spacing-lg;
    }
    .bw-hint {
      border-left: lighten($primary-color, 30%) solid 2px;
      color: $gray-color-dark;
      font-size: $font-size-sm;
      line-height: 1.5;
      margin-top: $layout-spacing;
      max-width: 40em;
      padding-left: $layout-spacing;
    }
  }
  .bw-hero-side {
    background-color: $gray-color-ultra-light;
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    padding: $layout-spacing $layout-spacing-lg;
  }
  .bw-step {
    align-items: center;
    display: flex;
    gap: $layout-spacing;
    padding: $layout-spacing-sm 0;
    & + .bw-step {
      border-top: $border-color solid $border-width;
    }
    strong {
      display: block;
      font-size: $font-size-sm;
    }
    small {
      color: $gray-color-dark;
    }
  }
  .bw-step-icon {
    align-items: center;
    background-color: $light-color;
    border: $border-color solid $border-width;
    border-radius: 50%;
    color: $primary-color;
    display: flex;
    flex: 0 0 auto;
    height: 1.6rem;
    justify-content: center;
    width: 1.6rem;
  }
  .bw-features {
    display: grid;
    gap: $layout-spacing;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .bw-feature {
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    padding: $layout-spacing $layout-spacing-lg;
    strong {
      display: block;
      margin-top: .3rem;
    }
    p {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      margin: .2rem 0 0;
    }
  }
  .bw-feature-icon {
    color: $primary-color;
  }
  .bw-compare {
    display: grid;
    gap: $layout-spacing;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .bw-side {
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    padding: $layout-spacing $layout-spacing-lg;
    ul {
      list-style: none;
      margin: $layout-spacing-sm 0 0;
      padding: 0;
    }
    li {
      align-items: baseline;
      color: $gray-color-dark;
      display: flex;
      font-size: $font-size-sm;
      gap: .4rem;
      margin-top: .3rem;
      svg {
        color: $gray-color;
        flex: 0 0 auto;
      }
      b {
        color: $body-font-color;
      }
    }
    &.bw-side-new {
      border-color: lighten($primary-color, 26%);
      li svg {
        color: $primary-color;
      }
    }
  }
  .bw-side-head {
    strong {
      display: block;
      font-size: $font-size-lg;
      font-weight: 600;
      margin-top: .2rem;
    }
    small {
      color: $gray-color-dark;
    }
  }
  .bw-side-tag {
    background-color: $gray-color-ultra-light;
    border-radius: 1rem;
    color: $gray-color-dark;
    font-size: $font-size-xs;
    font-weight: 600;
    padding: .1rem .35rem;
    &.is-new {
      background-color: lighten($primary-color, 36%);
      color: $secondary-color-dark;
    }
  }
  .bw-migration {
    align-items: baseline;
    background-color: $gray-color-ultra-light;
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    color: $gray-color-dark;
    display: flex;
    font-size: $font-size-sm;
    gap: $layout-spacing;
    margin-top: $layout-spacing;
    padding: $layout-spacing $layout-spacing-lg;
    strong {
      color: $body-font-color;
    }
  }
  .bw-migration-icon {
    color: $primary-color;
    flex: 0 0 auto;
  }
  .settings-section {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    font-weight: 600;
    letter-spacing: .04em;
    margin: $layout-spacing-lg 0 $layout-spacing-sm;
    text-transform: uppercase;
  }
  .settings-training {
    background-color: $gray-color-ultra-light;
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    display: grid;
    gap: $layout-spacing-lg;
    grid-template-columns: minmax(0, 22rem) minmax(0, 1fr);
    margin-top: $layout-spacing-lg;
    padding: $layout-spacing;
    .settings-training-media {
      aspect-ratio: 16 / 9;
      background-color: $dark-color;
      border-radius: $border-radius;
      display: block;
      overflow: hidden;
      position: relative;
      img {
        display: block;
        height: 100%;
        object-fit: cover;
        width: 100%;
      }
      .settings-training-play {
        align-items: center;
        background-color: rgba($light-color, .92);
        border-radius: 50%;
        color: $primary-color;
        display: flex;
        font-size: 1.1rem;
        height: 3rem;
        justify-content: center;
        left: 50%;
        padding-left: .2rem;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 3rem;
        z-index: 1;
      }
    }
    .settings-training-placeholder {
      align-items: center;
      color: $gray-color;
      display: flex;
      flex-direction: column;
      font-size: $font-size-sm;
      gap: .4rem;
      height: 100%;
      justify-content: center;
    }
    .settings-training-body {
      align-self: center;
      p {
        color: $gray-color-dark;
        margin: .3rem 0 .6rem;
      }
      a {
        font-weight: 600;
      }
    }
    .settings-training-eyebrow {
      color: $primary-color;
      font-size: $font-size-xs;
      font-weight: 700;
      letter-spacing: .06em;
      text-transform: uppercase;
    }
    .settings-training-title {
      font-size: $font-size-lg;
      font-weight: 600;
      margin-top: .15rem;
    }
  }
  @media screen and (max-width: $size-md) {
    .bw-hero,
    .bw-features,
    .bw-compare,
    .settings-training {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}
</style>
